// ClipMaster.io motion layer.
//
// VERBATIM COPY of the approved design exploration's clipmaster-motion.js
// (ClipMaster Theme Exploration/, origin/main). Kept as .js and unmodified
// so a future re-sync from the design is a clean diff rather than a merge;
// types live alongside in motion.d.ts.
//
// It reads window.Motion.animate. The original loaded that from a CDN, which
// our CSP (script-src 'self') blocks — src/app/shared/boot.tsx assigns it
// from the bundled npm package instead, so nothing is fetched cross-origin.
// Every effect is gated on prefers-reduced-motion inside initMotion().

//   data-reveal[="delayMs"]        fade/rise in when it enters the viewport (once)
//   data-reveal-x[="delayMs"]      same, sliding in from the left
//   data-words[="delayMs"]         split a heading into words and stagger them in
//   data-parallax="px"             scroll-linked vertical drift inside the nearest [data-parallax-scene]
//   data-scroll-scale="from"       scale from `from`→1 across the scene's scroll range
//   data-scroll-progress           width 0→100% across the whole document scroll
//   data-marquee[="seconds"]       duplicate children and loop them horizontally
//   data-tilt-scene + data-depth   pointer parallax layers (fine pointers only)
//   data-counter[+data-prefix/decimals]  count up when visible
//   data-grow="62%"                width grows from 0 when visible
const EASE = [0.22, 1, 0.36, 1];
const inViewport = (el) => { const r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < window.innerHeight * 0.92; };

export function initMotion() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(pointer: fine)').matches;
  const api = { ready: false, refresh() {}, pageEnter() {}, pulse() {}, dispose() {} };
  if (reduced) return api;
  const stops = []; const seen = new WeakSet(); let M = null; let observer = null; let disposed = false;

  const scan = () => {
    if (!M || disposed) return;
    const { animate, inView, scroll } = M;

    document.querySelectorAll('[data-words]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      const delay = Number(el.getAttribute('data-words') || 0) / 1000;
      if (!el.querySelector('[data-word]')) {
        const frag = document.createDocumentFragment();
        el.childNodes.forEach(node => {
          if (node.nodeType === 3) {
            node.textContent.split(/(\s+)/).forEach(tok => {
              if (!tok.trim()) { frag.appendChild(document.createTextNode(tok)); return; }
              const s = document.createElement('span'); s.setAttribute('data-word', ''); s.style.display = 'inline-block'; s.textContent = tok; frag.appendChild(s);
            });
          } else frag.appendChild(node.cloneNode(true));
        });
        el.textContent = ''; el.appendChild(frag);
      }
      const words = el.querySelectorAll('[data-word]');
      words.forEach(w => { w.style.opacity = '0'; });
      const run = () => words.forEach((w, i) => animate(w, { opacity: [0, 1], y: [22, 0], filter: ['blur(6px)', 'blur(0px)'] }, { duration: 0.7, delay: delay + i * 0.045, ease: EASE }));
      if (inViewport(el)) run(); else stops.push(inView(el, run, { margin: '0px 0px -10% 0px' }));
    });

    document.querySelectorAll('[data-reveal], [data-reveal-x]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      const x = el.hasAttribute('data-reveal-x');
      const delay = Number(el.getAttribute(x ? 'data-reveal-x' : 'data-reveal') || 0) / 1000;
      const from = x ? { opacity: [0, 1], x: [-28, 0] } : { opacity: [0, 1], y: [22, 0] };
      const run = () => { animate(el, from, { duration: 0.7, delay, ease: EASE }); el.setAttribute('data-revealed', '1'); };
      if (inViewport(el)) { run(); return; }
      stops.push(inView(el, run, { margin: '0px 0px -8% 0px' }));
    });

    document.querySelectorAll('[data-parallax]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      const d = Number(el.getAttribute('data-parallax') || 20);
      const scene = el.closest('[data-parallax-scene]') || el;
      stops.push(scroll(animate(el, { y: [d, -d] }, { ease: 'linear' }), { target: scene, offset: ['start end', 'end start'] }));
    });

    document.querySelectorAll('[data-scroll-scale]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      const from = Number(el.getAttribute('data-scroll-scale') || 0.94);
      const scene = el.closest('[data-parallax-scene]') || el;
      stops.push(scroll(animate(el, { scale: [from, 1] }, { ease: 'linear' }), { target: scene, offset: ['start end', 'center center'] }));
    });

    document.querySelectorAll('[data-scroll-progress]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      stops.push(scroll(animate(el, { scaleX: [0, 1] }, { ease: 'linear' })));
    });

    document.querySelectorAll('[data-marquee]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      const secs = Number(el.getAttribute('data-marquee') || 26);
      if (!el.getAttribute('data-marquee-ready')) {
        el.setAttribute('data-marquee-ready', '1');
        el.innerHTML += el.innerHTML;
      }
      stops.push(animate(el, { x: ['0%', '-50%'] }, { duration: secs, ease: 'linear', repeat: Infinity }));
    });

    document.querySelectorAll('[data-beams]').forEach(host => {
      if (seen.has(host)) return; seen.add(host);
      const pairs = Math.max(1, Math.round(Number(host.getAttribute('data-beams') || 6) / 2));
      const GRID = 88, rand = (a, b) => a + Math.random() * (b - a);
      const snap = (v) => Math.round(v / GRID) * GRID;
      // Beams travel in matched pairs so a horizontal and a vertical sweep the same
      // region together and visibly cross. Tracks avoid the centred hero copy.
      // Rows never cross the hero copy: the band occupied by the heading block
      // (measured live, with padding) is excluded from horizontal tracks.
      const copyBand = () => {
        const sec = host.closest('section') || host.parentElement;
        const copy = sec && sec.querySelector('h1');
        if (!copy) return null;
        const hr = host.getBoundingClientRect(), cr = (copy.closest('div') || copy).getBoundingClientRect();
        return [cr.top - hr.top - 56, cr.bottom - hr.top + 56];
      };
      const rowY = () => snap(window.innerHeight * rand(0.02, 0.98));
      // A row that runs through the copy band keeps its glow at the edges and
      // fades out across the centre, so nothing sweeps over the heading.
      const fadeForRow = (el, y) => {
        const band = copyBand();
        const inCopy = band && y > band[0] && y < band[1];
        const mask = inCopy ? 'linear-gradient(90deg, #000 0%, #000 14%, rgba(0,0,0,.15) 30%, transparent 42%, transparent 58%, rgba(0,0,0,.15) 70%, #000 86%, #000 100%)' : '';
        el.style.maskImage = mask; el.style.webkitMaskImage = mask;
      };
      const colX = () => { const w = window.innerWidth; return snap(w * (Math.random() < 0.5 ? rand(0.03, 0.3) : rand(0.7, 0.97))); };
      const make = (horizontal) => {
        const el = document.createElement('span');
        el.setAttribute('aria-hidden', 'true');
        const bright = Math.random() < 0.5;
        const glow = bright ? '.9' : '.6', shadow = bright ? '.55' : '.32';
        const len = rand(24, 52) + '%';
        const y = horizontal ? rowY() : 0;
        el.style.cssText = horizontal
          ? `position:absolute; left:0; top:${y}px; height:1px; width:${len}; opacity:0; background:linear-gradient(90deg, rgba(199,242,58,0), rgba(199,242,58,${glow}) 66%, ${bright ? 'rgba(255,255,255,.95) 84%,' : ''} rgba(199,242,58,0)); box-shadow:0 0 12px rgba(199,242,58,${shadow}); will-change:transform, opacity`
          : `position:absolute; top:0; left:${colX()}px; width:1px; height:${len}; opacity:0; background:linear-gradient(180deg, rgba(199,242,58,0), rgba(199,242,58,${glow}) 66%, ${bright ? 'rgba(255,255,255,.9) 84%,' : ''} rgba(199,242,58,0)); box-shadow:0 0 12px rgba(199,242,58,${shadow}); will-change:transform, opacity`;
        host.appendChild(el);
        if (horizontal) fadeForRow(el, y);
        return el;
      };
      const runPair = (a, b) => {
        if (disposed) return;
        const duration = rand(8, 14), delay = rand(0, 1.6);
        [[a, 'x'], [b, 'y']].forEach(([el, axis]) => {
          const keys = { opacity: [0, 1, 1, 0] }; keys[axis] = ['-40%', '140%'];
          const anim = animate(el, keys, { duration, delay, ease: 'linear' });
          stops.push(() => anim.stop && anim.stop());
        });
        setTimeout(() => {
          if (disposed) return;
          const ny = rowY(); a.style.top = ny + 'px'; fadeForRow(a, ny); b.style.left = colX() + 'px';
          a.style.opacity = '0'; b.style.opacity = '0';
          runPair(a, b);
        }, (duration + delay) * 1000 + rand(200, 1400));
      };
      for (let i = 0; i < pairs; i++) {
        const a = make(true), b = make(false);
        setTimeout(() => runPair(a, b), i * rand(600, 2200));
      }
    });

    document.querySelectorAll('[data-hscroll]').forEach(track => {
      if (seen.has(track)) return; seen.add(track);
      const scene = track.closest('[data-hscroll-scene]');
      const stage = track.closest('[data-hscroll-stage]');
      const viewport = track.parentElement;
      if (!scene || !stage || !viewport) return;
      // Pinned horizontal gallery: the scene is tall enough to absorb the track's
      // overflow, the stage sticks, and scroll progress drives the track sideways.
      let ctrl = null, distance = 0;
      const layout = () => {
        if (window.innerWidth < 760) { scene.style.height = ''; viewport.style.overflowX = 'auto'; track.style.transform = ''; if (ctrl) { ctrl(); ctrl = null; } return; }
        viewport.style.overflowX = 'hidden';
        track.style.transform = 'translateX(0px)';
        // Match the heading's gutter exactly (100vw-based padding drifts by the scrollbar width).
        const head = stage.querySelector('h2');
        if (head) {
          const gut = Math.max(24, Math.round(head.getBoundingClientRect().left));
          track.style.paddingLeft = gut + 'px';
          track.style.paddingRight = gut + 'px';
        }
        const endPad = 0;
        distance = Math.max(0, track.scrollWidth - viewport.clientWidth) + endPad;
        scene.style.height = (stage.offsetHeight + distance) + 'px';
        if (ctrl) ctrl();
        const travel = Math.max(0, track.scrollWidth - viewport.clientWidth);
        // Driven from measured pin progress rather than scroll()'s viewport range:
        // while the scene's top is still below the sticky offset the track stays at 0,
        // so card 01 remains flush with the heading until the stage is actually frozen.
        let raf2 = 0;
        const apply = () => {
          raf2 = 0;
          // Progress starts where the stage actually pins (its sticky offset), so the
          // freeze begins when the header's lower edge meets the previous section's end.
          const offset = parseFloat(getComputedStyle(stage).top) || 0;
          const top = scene.getBoundingClientRect().top;
          const past = offset - top;
          const pr = travel > 0 ? Math.min(1, Math.max(0, past / travel)) : 0;
          track.style.transform = 'translateX(' + (-travel * pr) + 'px)';
        };
        const onScroll = () => { if (!raf2) raf2 = requestAnimationFrame(apply); };
        window.addEventListener('scroll', onScroll, { passive: true });
        stops.push(() => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf2); });
        ctrl = () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf2); };
        apply();
      };
      layout();
      // Re-measure once fonts and media settle so the first pin always starts from card 01.
      setTimeout(layout, 300);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (!disposed) layout(); }).catch(() => {});
      const onResize = () => requestAnimationFrame(layout);
      window.addEventListener('resize', onResize);
      stops.push(() => window.removeEventListener('resize', onResize));
    });

    document.querySelectorAll('[data-counter]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      const target = Number(el.getAttribute('data-counter')); const prefix = el.getAttribute('data-prefix') || ''; const dec = Number(el.getAttribute('data-decimals') || 0);
      stops.push(inView(el, () => { animate(0, target, { duration: 1.5, ease: EASE, onUpdate: v => { el.textContent = prefix + v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }); } }); }));
    });

    document.querySelectorAll('[data-grow]').forEach(el => {
      if (seen.has(el)) return; seen.add(el);
      const w = el.getAttribute('data-grow');
      stops.push(inView(el, () => { animate(el, { width: ['0%', w] }, { duration: 1.4, delay: 0.3, ease: EASE }); }));
    });

    if (fine) document.querySelectorAll('[data-spotlight-scene]').forEach(scene => {
      if (seen.has(scene)) return; seen.add(scene);
      const spots = Array.from(scene.querySelectorAll('[data-spotlight]')).filter(el => el.closest('[data-spotlight-scene]') === scene);
      if (!spots.length) return;
      spots.forEach(s => { s.style.opacity = '0'; });
      // A spotlight inside a fixed layer tracks viewport coordinates; one inside a
      // flowing section tracks coordinates local to that section.
      const onMove = (e) => { const r = scene.getBoundingClientRect(); spots.forEach(spot => { const fixed = spot.getAttribute('data-spotlight') === 'fixed'; animate(spot, { x: fixed ? e.clientX : e.clientX - r.left, y: fixed ? e.clientY : e.clientY - r.top, opacity: 1 }, { duration: 0.9, ease: EASE }); }); };
      const onLeave = () => spots.forEach(spot => animate(spot, { opacity: 0 }, { duration: 0.8, ease: EASE }));
      scene.addEventListener('pointermove', onMove); scene.addEventListener('pointerleave', onLeave);
      stops.push(() => { scene.removeEventListener('pointermove', onMove); scene.removeEventListener('pointerleave', onLeave); });
    });

    if (fine) document.querySelectorAll('[data-tilt-scene]').forEach(scene => {
      if (seen.has(scene)) return; seen.add(scene);
      const layers = Array.from(scene.querySelectorAll('[data-depth]'));
      const onMove = (e) => { const r = scene.getBoundingClientRect(); const dx = (e.clientX - r.left) / r.width - 0.5, dy = (e.clientY - r.top) / r.height - 0.5; layers.forEach(l => { const d = Number(l.getAttribute('data-depth') || 20); animate(l, { x: dx * d, y: dy * d }, { duration: 0.7, ease: EASE }); }); };
      const onLeave = () => layers.forEach(l => animate(l, { x: 0, y: 0 }, { duration: 0.9, ease: EASE }));
      scene.addEventListener('pointermove', onMove); scene.addEventListener('pointerleave', onLeave);
      stops.push(() => { scene.removeEventListener('pointermove', onMove); scene.removeEventListener('pointerleave', onLeave); });
    });
  };

  const activate = () => {
    if (disposed) return;
    M = window.Motion; api.ready = true;
    document.querySelectorAll('[data-reveal]').forEach(el => { if (inViewport(el)) { el.setAttribute('data-revealed', '1'); } });
    document.documentElement.setAttribute('data-motion-ready', '1');
    scan();
    let raf = 0; observer = new MutationObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(scan); });
    observer.observe(document.body, { childList: true, subtree: true });
  };
  let tries = 0; const wait = () => { if (disposed) return; if (window.Motion && window.Motion.animate) activate(); else if (tries++ < 100) setTimeout(wait, 60); };
  wait();

  api.refresh = () => { if (M) requestAnimationFrame(scan); };
  api.pageEnter = (el) => { if (M && el) M.animate(el, { opacity: [0, 1], y: [10, 0] }, { duration: 0.4, ease: EASE }); };
  api.pulse = (el) => { if (M && el) M.animate(el, { scale: [0.97, 1], opacity: [0, 1] }, { duration: 0.35, ease: EASE }); };
  api.dispose = () => { disposed = true; if (observer) observer.disconnect(); stops.forEach(s => { try { typeof s === 'function' && s(); } catch (e) {} }); document.documentElement.removeAttribute('data-motion-ready'); };
  return api;
}
